import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useState } from "react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface RequestReviewsSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function RequestReviewsSheet({ isOpen, onClose }: RequestReviewsSheetProps) {
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [messageText, setMessageText] = useState(
    "We value your feedback! Please take a moment to share your experience with our cleaning services. Your review helps us improve and lets others know about our work."
  );

  const [isSending, setIsSending] = useState(false);

  const { toast } = useToast();
  const sendReviewRequest = useAction(api.reviewRequests.sendReviewRequest);

  // Fetch real client data from businesses table
  const businesses = useQuery(api.businesses.list) || [];

  // Filter to only include clients (not leads)
  const clients = businesses.filter((business: any) => business.type === "client");
  const isLoading = businesses === undefined;

  const handleSelectClient = (clientId: string) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter((id) => id !== clientId));
    } else {
      setSelectedClients([...selectedClients, clientId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map((client: any) => client._id));
    }
  };

  const handleSendRequests = async () => {
    if (selectedClients.length === 0) {
      toast({
        title: "No clients selected",
        description: "Please select at least one client to send review requests.",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    let successCount = 0;
    let failureCount = 0;

    try {
      // Send review requests to each selected client
      for (const clientId of selectedClients) {
        const client = clients.find((c: any) => c._id === clientId);
        if (!client || !client.contactEmail) {
          failureCount++;
          continue;
        }

        try {
          const result = await sendReviewRequest({
            businessId: clientId as Id<"businesses">,
            recipientEmail: client.contactEmail,
            recipientName: client.contactName || client.name,
            customMessage: messageText,
          });

          if (result.success) {
            successCount++;
          } else {
            failureCount++;
            console.error(`Failed to send to ${client.contactEmail}:`, result.error);
          }
        } catch (error) {
          failureCount++;
          console.error(`Error sending to ${client.contactEmail}:`, error);
        }
      }

      // Show success/failure toast
      if (successCount > 0 && failureCount === 0) {
        toast({
          title: "Review requests sent!",
          description: `Successfully sent ${successCount} review request${successCount > 1 ? "s" : ""}.`,
        });
      } else if (successCount > 0 && failureCount > 0) {
        toast({
          title: "Partially successful",
          description: `Sent ${successCount} requests successfully, ${failureCount} failed.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Failed to send requests",
          description: "All review requests failed to send. Please check client email addresses.",
          variant: "destructive",
        });
      }

      // Reset form and close if any were successful
      if (successCount > 0) {
        setSelectedClients([]);
        setMessageText(
          "We value your feedback! Please take a moment to share your experience with our cleaning services. Your review helps us improve and lets others know about our work."
        );
        onClose();
      }
    } catch (error) {
      console.error("Error sending review requests:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while sending review requests.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Request Client Reviews</SheetTitle>
          <SheetDescription>
            Send review requests to your clients via email with dynamically generated review links.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div>
              <Label className="text-base">Select Clients</Label>
              <div className="mt-2 overflow-hidden rounded-md border">
                {isLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : clients.length === 0 ? (
                  <div className="p-8 text-center">
                    <p className="text-sm text-gray-500">No clients found</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center border-b bg-gray-50 p-3">
                      <Checkbox
                        id="select-all"
                        checked={selectedClients.length === clients.length && clients.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                      <label htmlFor="select-all" className="ml-2 text-sm font-medium">
                        Select All Clients ({clients.length})
                      </label>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {clients.map((client: any) => (
                        <div
                          key={client._id}
                          className="flex items-center justify-between border-b p-3 last:border-b-0"
                        >
                          <div className="flex items-center">
                            <Checkbox
                              id={`client-${client._id}`}
                              checked={selectedClients.includes(client._id)}
                              onCheckedChange={() => handleSelectClient(client._id)}
                            />
                            <label
                              htmlFor={`client-${client._id}`}
                              className="ml-2 text-sm font-medium"
                            >
                              {client.name}
                            </label>
                          </div>
                          <span className="text-sm text-gray-500">
                            {client.contactEmail || "No email"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {selectedClients.length} clients selected
              </p>
            </div>

            <div>
              <Label className="text-base">Message</Label>
              <Textarea
                className="mt-2"
                rows={4}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onClose} disabled={isSending}>
                Cancel
              </Button>
              <Button
                onClick={handleSendRequests}
                disabled={selectedClients.length === 0 || isSending}
                className="gap-2"
              >
                {isSending && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSending ? "Sending..." : "Send Requests"}
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
